import React, { createContext, useCallback, useContext } from 'react';
import {
	sendSubmissionQuery,
	useAxiosInstance,
	usePoMutation,
} from '@gov-nx/api';
import { useExtraConfigContext } from '@gov-nx/core/app';
import { useDownloadEvents, useMessageEvents } from '@gov-nx/core/events';
import { useApplicantLoader, useProcessControl } from '@gov-nx/core/hooks';
import {
	getRequiredDataBoxes,
	LocalizeNameSpaceTypes,
	useLocale,
} from '@gov-nx/core/service';
import type { Nullable } from '@gov-nx/core/types';
import { is } from '@gov-nx/core/types';
import { useDataBoxAccess } from '@gov-nx/module/data-box';
import { useAutorizaceStore } from '@gov-nx/store';
import { useAutorizaceContext } from 'libs/component/web/src/Autorizace/Context';
import type { ServiceCode } from '../definitions/codes';
import { FormInstance, prepareSubmitData } from './FormDefinitions';
import type {
	FormData,
	ServiceContextControls,
	ServiceContextTypes,
} from './service.types';

export const ServiceContext =
	createContext<Nullable<ServiceContextTypes>>(null);

export interface ServiceContextProviderProps {
	children: React.ReactNode;
	code: ServiceCode;
}

export function PotvrzeniOStudiuContextProvider({
	children,
	code,
}: ServiceContextProviderProps) {
	const { t } = useLocale(code);
	const { t: tf } = useLocale(LocalizeNameSpaceTypes.Form);
	const { toastMessageSuccess } = useMessageEvents();
	const {
		extraConfig: { application },
	} = useExtraConfigContext();
	const requiredDataBoxes = getRequiredDataBoxes(code, application);
	const { downloadBlob } = useDownloadEvents();
	const { areAnyConnected } = useDataBoxAccess({
		requiredTypes: requiredDataBoxes,
	});
	const { axiosInstance } = useAxiosInstance();

	const { controls, setControls } = useProcessControl<ServiceContextControls>({
		initialLoading: true,
		downloadLoading: false,
		downloadError: null,
	});

	const isProcessing = controls.processLoading || controls.downloadLoading;

	const authContext = useAutorizaceContext();
	const getServiceAuthorizationType = useAutorizaceStore(
		'getServiceAuthorizationType'
	);
	const AUTHORIZATION_SERVICE_NAME = 'POTVRZENI_O_STUDIU';
	const requiresConsent =
		getServiceAuthorizationType(AUTHORIZATION_SERVICE_NAME) === 'NIZKA';

	const { formDefinition, fields } = FormInstance({
		code,
		dataBoxTypes: requiredDataBoxes,
		isDisabled: isProcessing,
		requiresConsent,
	});

	useApplicantLoader({
		onError: (initialError) => {
			setControls({ initialLoading: false, initialError });
		},
		onLoaded: () => {
			if (controls.initialLoading) {
				setControls({ initialLoading: false });
			}
		},
	});

	const submitMutation = usePoMutation({
		mutationFn: async (data: FormData) => {
			const prepared = prepareSubmitData(data);
			return sendSubmissionQuery(axiosInstance)(prepared);
		},
		onError: (error) => {
			setControls({ processError: error, processLoading: false });
		},
		onSuccess: async (data) => {
			setControls({ processLoading: false });

			const nonHighAuthorizationFlow = () => {
				toastMessageSuccess(t('formular.zprava.odeslano'));
				formDefinition.formReset();
			};

			if (authContext) {
				authContext.onInitAuthorization(
					AUTHORIZATION_SERVICE_NAME,
					data.headers['x-autorizaceid'],
					nonHighAuthorizationFlow
				);
			} else {
				nonHighAuthorizationFlow();
			}
		},
	});

	const handleSubmit = useCallback(async () => {
		const values = formDefinition.formMethods.getValues();
		if (!is(values.odesilatelDSId)) {
			formDefinition.formMethods.setError('odesilatelDSId', {
				type: 'custom',
				message: tf('data-box.validations.vyberte-datovou-schranku'),
			});
		} else {
			setControls({ processError: null, processLoading: true });
			submitMutation.mutate(formDefinition.formMethods.getValues());
		}
	}, [formDefinition.formMethods, tf, setControls, submitMutation]);

	const handleDownload = useCallback(async () => {
		setControls({ downloadError: null, downloadLoading: true });
		return downloadBlob({
			data: prepareSubmitData(formDefinition.formMethods.getValues()),
			onSuccess: () => {
				setControls({ downloadLoading: false });
				formDefinition.formReset();
			},
			onError: (error) => {
				setControls({ downloadError: error, downloadLoading: false });
			},
		});
	}, [downloadBlob, formDefinition, setControls]);

	const onSubmit = formDefinition.formMethods.handleSubmit(handleSubmit);
	const onDownload = formDefinition.formMethods.handleSubmit(handleDownload);
	const typeOfSchool = formDefinition.formMethods.watch('skolaTyp');

	return (
		<ServiceContext.Provider
			value={{
				code,
				formDefinition,
				submitMutation,
				onSubmit,
				onDownload,
				controls,
				setControls,
				requiredDataBoxes,
				isAnyDataboxConnected: areAnyConnected,
				fields,
				isProcessing,
				typeOfSchool,
				requiresConsent,
			}}>
			{children}
		</ServiceContext.Provider>
	);
}

export const usePotvrzeniStudiuContext = (): ServiceContextTypes =>
	useContext(ServiceContext) as ServiceContextTypes;
